import {isProduction} from '@shared-utils/helpers'

export const accessRules = {
  hasAccessToDashboard: auth => (auth.isAgencyClient && auth.authUser.subrole === 'admin') || auth.isMediaBuyer,
  hasAccessToPerformanceReport: (auth, to) => auth.performanceReportAvailability?.[to?.params?.platform] === true,
  hasAccessToTeam: auth => auth.isCompanyAdmin,
  hasAccessToMyRequests: auth => auth.userHasCompany,
  hasAccessToCreativeModerationRequest(auth) {
    return auth.isComplianceManager || auth.isAgency || auth.isBusinessUnitAPAC || auth.isDeveloper
  },
  hasAccessToClientPreModerationList: auth =>
    auth.isComplianceManager || auth.isAgency || auth.isBusinessUnitAPAC || auth.isDeveloper,
  hasAccessToPreModerationRules: auth => auth.isComplianceManager,
  canManagePreModerationRules: auth => auth.isComplianceTeamLead,
  hasAccessToComplianceAppeals: auth =>
    auth.isComplianceManager || auth.isSuperAdmin || auth.hasAccessToSupportToolsPage,
  hasAccessToRequestsPage: auth => auth.hasAccessToRequestsPage,
  hasAccessToAdAccountRequests: auth =>
    auth.userHasCompany || auth.isAgency || auth.isBusinessUnitAPAC || auth.isDeveloper || auth.isComplianceManager,
  hasAccessToAccountRenameRequests: auth =>
    auth.userHasCompany || auth.isAgency || auth.isBusinessUnitAPAC || auth.isDeveloper,
  hasAccessToTopUpRequestsPage: auth =>
    auth.userHasCompany || auth.isAgency || auth.isBusinessUnitAPAC || auth.isDeveloper || auth.isFinance,
  hasAccessToWhiteListRequestsPage: auth => {
    return (
      auth.userHasCompany || auth.isAgency || auth.isBusinessUnitAPAC || auth.isDeveloper || auth.isComplianceManager
    )
  },
  hasAccessToSharePermissionRequestsPage: auth =>
    auth.userHasCompany || auth.isAgency || auth.isBusinessUnitAPAC || auth.isDeveloper,
  hasAccessToFBPageRequestsPage: auth =>
    auth.userHasCompany || auth.isAgency || auth.isBusinessUnitAPAC || auth.isDeveloper,
  hasAccessToFBPageRequestFormPage: auth => auth.canSeeFBPageRequestForm || auth.isDeveloper,
  hasAccessToCreativeModerationList: auth => auth.canWorkWithCreatives && !auth.isAgency && !auth.isComplianceManager,

  hasAccessToCreativeModerationProfile(auth) {
    return accessRules.hasAccessToCreativeModerationList(auth) || accessRules.hasAccessToCreativeModerationRequest(auth)
  },

  hasAccessToSupportTools: auth => auth.hasAccessToSupportToolsPage,
  hasAccessToCompanySettingsTool: auth => auth.isSuperAdmin,
  canForceChangeAdAccountRequestStatus: auth => auth.isTechSupportOrQA,
  canDeleteAdAccountRequest: auth => auth.isTechSupportOrQA,
  hasAccessToCreditCards: auth => auth.isCreditCardManager,

  hasAccessToPaymentsDailyBalance: auth => auth.isAgencyClient,
  hasAccessToFinancePage: auth => auth.canUseInvoices,
  hasAccessToInvoices: auth => auth.isFinance,

  // Retainers are hidden behind the can_use_retainers policy flag until rollout.
  hasAccessToRetainers: auth => auth.canUseRetainers,
  hasAccessToRetainerPlans: auth => auth.canUseRetainers && auth.isSuperAdmin,
  canEditRetainerConditions: auth => auth.canEditRetainerConditions,
  hasAccessToClientOnboarding: auth => auth.canWorkWithOnboardings,
  hasAccessToOnboardingDocument: auth => auth.canWorkWithOnboardings,
  hasAccessToCompaniesPage: auth => auth.hasAccessToCompaniesPage,
  hasAccessToEmployeeList: auth => auth.canManageEmployees,

  hasAccessToFacebookPages: auth => auth.isTechDepartment || auth.isAgencyAssistance,
  hasAccessToAssetsPage: auth => auth.hasAccessToAssetsPage,
  hasAccessToAccountConnections: auth =>
    auth.isAgency || auth.isBusinessUnitAPAC || auth.isDeveloper || auth.isAnalyst || auth.isComplianceManager,

  hasAccessToTracker: auth => auth.isDeveloperOrInternalCompany && !auth.isGoogleUser,
  hasAccessToLandings: auth => auth.isDeveloper || auth.isMediaBuyer,
  hasAccessToAdAlerts: auth => auth.isDeveloper || auth.isMediaBuyer,
  hasAccessToFacebookLicense: auth => auth.accessToFacebookLicenses,
  hasAccessToBusinessManagers: auth => auth.isAgency || auth.isDeveloper || auth.isAnalyst,
  canSwitchBusinessManagerSource: auth => auth.isCoreTeam,
  canManageBusinessManagers: auth => auth.isAgencyAssistance,
  canSeeAssetSellerPrice: auth => auth.isAgencyAssistance || auth.isAgencyAssistanceTeamLead,
  canSeeFacebookProfileAttributes: auth => auth.isAgencyAssistance || auth.isAgencyAssistanceTeamLead,

  hasAccessToLandingTask: auth => auth.canCreateNewLandingTask,
  hasAccessToLandingCopyTask: auth => auth.canCreateCopyLandingTask,
  hasAccessToABTestTask: auth => auth.canCreateABtestTask,
  hasAccessToInternalTask: auth => auth.canCreateInternalTask,
  hasAccessToCreativesTask: auth => auth.canCreateCreativeTask,
  hasAccessToShortTask: auth => auth.canCreateShortTask,
  hasAccessToSMMTask: auth => auth.canCreateSMMTask,
  hasAccessToHandsOnTask: auth => auth.isAgency || auth.isBusinessUnitAPAC,
  hasAccessToCouponRequest: auth => auth.canUseCoupons,
  hasAccessToAgencyGeneralTask: auth => auth.canCreateAgencyGeneralTask,
  canChangeClientPreModerationStatus: auth => auth.canModerateClientPreModeration,
  hasAccessToPresentationTask: auth =>
    auth.isPoint2WebMarketing ||
    auth.isPerformaceNetworkDepartment ||
    auth.isDesignUnit ||
    auth.isHRUnit ||
    auth.isCopywriterUnit ||
    auth.isAgency ||
    (auth.isBusinessUnitAPAC && !auth.isAgencyAssistance) ||
    (auth.isDeveloper && !isProduction()),

  hasAccessToPaymentRequestTask: auth => auth.isPoint2WebEmployee,
  hasAccessToTasks: auth => auth.isPoint2WebEmployee,
  hasAccessToReports: auth => auth.isPoint2WebEmployee,
  hasAccessToCRDomain: auth => auth.canUseDomains && auth.fromSFCompany,
  hasAccessToDomains: auth => auth.canUseDomains,
  hasAccessToAppeals: auth => auth.canUseAppeals,
  hasAccessToFacebookProfiles: auth => auth.canSeeFacebookPages,
  hasAccessToSFLinks: auth => auth.fromSFCompany || auth.isDeveloper,

  hasAccessToTools: auth => auth.isPoint2WebEmployee,
  canEditEmailSignatureAvatar: auth => auth.canEditEmailSignatureAvatar,
  hasAccessToClientsDashboard: auth => auth.canAccessWeeklyClientsUpdate,
  hasAccessToPixels: auth => auth.isTechDepartment || auth.isMediaBuyer || auth.isAgency,
  hasAccessToConversions: auth => auth.isTechDepartment || auth.isMediaBuyer || auth.isAgency,
  hasAccessToClickupPage: auth => auth.isPoint2WebEmployee,
  hasAccessToOtherRequestList: auth =>
    auth.isAgency || auth.isBusinessUnitAPAC || auth.isDeveloper || auth.isComplianceManager,
  canCreateCreativeModerationRequest(auth) {
    return (
      auth.isBusinessUnitAPAC ||
      auth.isSaleManager ||
      auth.isMediaPlannerOrCSM ||
      auth.isComplianceManager ||
      auth.isAgencyClient
    )
  },
}
